import React, { useState } from 'react';
import { HiX, HiDocumentText } from "react-icons/hi";
import { PuzzleIcon } from 'lucide-react';
import { Drawer } from "../../components/Modals";
import CustomButton from '../../components/common/CustomButton';
import './CreateWorkUnit.css';
import CreateEpicNormalModal from './components/CreateEpicNormalModal';
import CreateEpicAdvancedModalBody from '../AdvancedEpic/CreateEpicAdvancedModalBody';
import CreateFeatureModal from './components/CreateFeatureModal';
import { IoLayers } from 'react-icons/io5';
import { BsLayersFill } from 'react-icons/bs';
import { Alert } from 'flowbite-react';
import { FaInfoCircle } from 'react-icons/fa';

interface CreateWorkUnitProps {
  isVisible: boolean;
  onClose: () => void;
  onSuccess?: (data?: any) => void;
  preventRedirect?: boolean;
}

type WorkUnitType = 'epic' | 'feature' | null;
type EpicMode = 'normal' | 'advanced' | null;
type SectionState = 'home' | 'normal_epic' | 'advanced_epic' | 'feature';

const HELP_MESSAGES = {
  normal_epic: {
    title: "Normal Epic Mode",
    description:
      "Best for high-level planning where you want to group tasks directly under an Epic. This bypasses the Feature layer, making it ideal for simpler projects or initial brainstorming phases where structure is less critical.",
  },
  advanced_epic: {
    title: "Advanced Epic Mode",
    description:
      "The standard hierarchy for complex projects. Creates a full structure: Epic -> Features -> Tasks. Use this when you need granular control, distinct feature sets, and detailed tracking across multiple workstreams.",
  },
  feature: {
    title: "Feature Creation",
    description:
      "Create a focused increment of value. Features are the building blocks of Epics and containers for Tasks.",
  },
};

export const CreateWorkUnit: React.FC<CreateWorkUnitProps> = ({
  isVisible,
  onClose,
  onSuccess,
  preventRedirect = false
}) => {
  const [selection, setSelection] = useState<{ type: WorkUnitType, mode: EpicMode }>({ type: "epic", mode: "normal" });
  const [workUnitName, setWorkUnitName] = useState('');
  
  // Single state to manage the current view
  const [currentSection, setCurrentSection] = useState<SectionState>('home');

  const handleSelectEpicMode = (mode: 'normal' | 'advanced') => {
    setSelection({ type: 'epic', mode });
    // If not on home page, switch immediately
    if (currentSection !== 'home') {
        setCurrentSection(mode === 'normal' ? 'normal_epic' : 'advanced_epic');
    }
  };

  const handleSelectFeature = () => {
    setSelection({ type: 'feature', mode: null });
    // If not on home page, switch immediately
    if (currentSection !== 'home') {
        setCurrentSection('feature');
    }
  };

  const handleCreate = () => {
    if (selection.type === 'epic') {
        if (selection.mode === 'normal') {
            setCurrentSection('normal_epic');
        } else if (selection.mode === 'advanced') {
            setCurrentSection('advanced_epic');
        }
    } else if (selection.type === 'feature') {
        setCurrentSection('feature');
    }
  };

  const handleSuccess = (data?: any) => {
      const id = data?.project_id || data?.roadmap_id || data?.feature_id;
      onSuccess?.({...data, id});
      onClose();
  }

  // Reset state when closing (optional, but good practice if drawer stays mounted)
  const handleClose = () => {
      setCurrentSection('home');
      // setSelection({ type: "epic", mode: "normal" });
      // onClose();
  }

  const handleDrawerClose=()=>{
    onClose();
  }
  return (
    <>
      <Drawer isOpen={isVisible} onClose={handleDrawerClose} position="right">
        <div className="work-unit-drawer bg-white">
          {/* Header */}
          <header className="work-unit-drawer__header flex !flex-col">
            <div className="work-unit-drawer__header-content">
              <h2 className="work-unit-drawer__title">
                Create{" "}
                {currentSection === "normal_epic"
                  ? "Normal Epic"
                  : currentSection === "advanced_epic"
                    ? "Advanced Epic"
                    : currentSection === "feature"
                      ? "Feature"
                      : "New Work Item"}
              </h2>
              <button
                type="button"
                className="work-unit-drawer__close-button"
                onClick={handleDrawerClose}
              >
                <HiX className="text-xl" />
              </button>
            </div>
          </header>

          {currentSection !== "home" ? (
            <div className="flex-1 overflow-hidden relative">
              {currentSection === "normal_epic" && (
                <CreateEpicNormalModal
                  isVisible={true}
                  setShowModal={handleClose}
                  project={{ project_name: workUnitName }}
                  onProjectUpdate={handleSuccess}
                  onCreate={handleSuccess}
                  preventRedirect={preventRedirect}
                  type="normal_epic"
                />
              )}

              {currentSection === "advanced_epic" && (
                <CreateEpicAdvancedModalBody
                  isVisible={true}
                  setShowModal={handleClose}
                  roadmap={{ name: workUnitName }}
                  setSelectedRoadmap={handleSuccess}
                  onCreate={handleSuccess}
                  preventRedirect={preventRedirect}
                  type="advanced_epic"
                />
              )}

              {currentSection === "feature" && (
                <CreateFeatureModal
                  isVisible={true}
                  setShowModal={handleClose}
                  project={{ project_name: workUnitName }}
                  onProjectUpdate={handleSuccess}
                  onCreate={handleSuccess}
                  preventRedirect={preventRedirect}
                  type="feature"
                />
              )}
            </div>
          ) : (
            <>
              {/* Content */}
              <div className="work-unit-drawer__content animate-fade-in">
                {/* Create an Epic Section */}
                <section className="work-unit-drawer__section">
                  {/* <div className="work-unit-drawer__section-header">
                    <div className="work-unit-drawer__icon-box">
                      <div className="grid grid-cols-2 gap-0.5">
                        <div className="w-1.5 h-1.5 rounded-sm bg-blue-500"></div>
                        <div className="w-1.5 h-1.5 rounded-sm bg-blue-500"></div>
                        <div className="w-1.5 h-1.5 rounded-sm bg-blue-500"></div>
                        <div className="w-1.5 h-1.5 rounded-sm bg-blue-500"></div>
                      </div>
                    </div>
                    <div>
                      <h3 className="work-unit-drawer__section-title">
                        Create an Epic
                      </h3>
                      <p className="work-unit-drawer__section-subtitle">
                        High-level strategic work that spans multiple features.
                      </p>
                    </div>
                  </div> */}

                  <p className="text-sm text-light-text-secondary dark:text-gray-400 mb-6 mt-2 ml-1">
                    Select Interaction Mode - Define the depth of planning for
                    this Epic.
                  </p>

                  <div className="work-unit-drawer__mode-grid">
                    {/* Normal Mode Card */}
                    <div
                      className={`work-unit-drawer__card ${selection.type === "epic" && selection.mode === "normal" ? "work-unit-drawer__card--selected" : ""}`}
                      onClick={() => handleSelectEpicMode("normal")}
                    >
                      <div className="work-unit-drawer__card-icon-container">
                        <BsLayersFill className="text-violet-400 text-[30px]" />
                      </div>
                      <span className="work-unit-drawer__card-title">
                        Normal Epic
                      </span>
                      <div className="work-unit-drawer__flow-pill">
                        Epic <span className="mx-1">→</span> Task
                      </div>
                      <p className="work-unit-drawer__card-description">
                        Quickly create a work unit without intermediate
                        features.
                      </p>
                    </div>

                    {/* Advanced Mode Card */}
                    <div
                      className={`work-unit-drawer__card ${selection.type === "epic" && selection.mode === "advanced" ? "work-unit-drawer__card--selected" : ""}`}
                      onClick={() => handleSelectEpicMode("advanced")}
                    >
                      <div className="work-unit-drawer__card-icon-container">
                        <IoLayers className="text-violet-400 text-[30px]" />
                      </div>
                      <span className="work-unit-drawer__card-title">
                        Advanced Epic
                      </span>
                      <div className="work-unit-drawer__flow-pill">
                        Epic <span className="mx-1">→</span> Feature{" "}
                        <span className="mx-1">→</span> Task
                      </div>
                      <p className="work-unit-drawer__card-description">
                        Full roadmap creation with hierarchical breakdown.
                      </p>
                    </div>
                    <div
                      className={`work-unit-drawer__card ${selection.type === "feature" ? "work-unit-drawer__card--selected" : ""}`}
                      onClick={() => handleSelectFeature()}
                    >
                      <div className="work-unit-drawer__card-icon-container">
                        <PuzzleIcon className="text-sky-400 text-[30px]" />
                      </div>
                      <span className="work-unit-drawer__card-title">
                        Feature
                      </span>
                      <div className="work-unit-drawer__flow-pill">
                        Feature <span className="mx-1">→</span> Task
                      </div>
                      <p className="work-unit-drawer__card-description">
                        Full roadmap creation with hierarchical breakdown.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Explanatory Message Section */}
                <div className="pb-6 min-h-[100px] ">
                  {selection.type && (
                    <div className="relative bg-blue-50 dark:bg-blue-900/10  rounded-xl p-4 animate-fade-in shadow-sm  border-2 border-blue-500">
                      {/* Arrow pointing up to the grid */}
                      <div
                        className="absolute -top-[9px] w-4 h-4 bg-blue-50 dark:bg-blue-900/10 border-t-2 border-l-2 border-blue-500 transform rotate-45 transition-all duration-300 "
                        style={{
                          left:
                            selection.type === "epic" &&
                            selection.mode === "normal"
                              ? "16%"
                              : selection.type === "epic" &&
                                  selection.mode === "advanced"
                                ? "50%"
                                : "83%",
                        }}
                      ></div>

                      <div className="flex gap-3 ">
                        <div className="mt-1 flex-shrink-0">
                          <HiDocumentText className="text-blue-500 text-xl" />
                        </div>
                        <div className="w-full">
                          <h4 className="font-medium text-blue-900 dark:text-blue-100 text-sm mb-1">
                            {
                              HELP_MESSAGES[
                                selection.type === "epic"
                                  ? selection.mode === "normal"
                                    ? "normal_epic"
                                    : "advanced_epic"
                                  : "feature"
                              ]?.title
                            }
                          </h4>
                          <p className="text-blue-800/80 dark:text-blue-200/80 text-xs leading-relaxed max-w-prose">
                            {
                              HELP_MESSAGES[
                                selection.type === "epic"
                                  ? selection.mode === "normal"
                                    ? "normal_epic"
                                    : "advanced_epic"
                                  : "feature"
                              ]?.description
                            }
                          </p>

                          <Alert
                            color={"info"}
                            icon={FaInfoCircle}
                            className="text-xs w-full mt-2"
                          >
                            Once created, you can't change the type of the work
                            unit.
                          </Alert>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <footer className="work-unit-drawer__footer">
                <CustomButton
                  variant="cancel"
                  onClick={handleDrawerClose}
                  className="work-unit-drawer__footer-button"
                >
                  Cancel
                </CustomButton>

                <CustomButton
                  onClick={handleCreate}
                  disabled={!selection.type}
                  className="work-unit-drawer__footer-button"
                >
                  Next
                </CustomButton>
              </footer>
            </>
          )}
        </div>
      </Drawer>
    </>
  );
};

export default CreateWorkUnit;
